import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Stack, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { fget } from '../../../axios/axiosCalls';
import NewBookings from '../../../components/dashboards/modern/NewBookings';
import TownWiseAvailability from '../../../components/dashboards/modern/TownWiseAvailability';
import NewBookingsGraph from '../../../components/dashboards/modern/NewBookingsGraph';
import NewBookingsPerYear from '../../../components/dashboards/modern/NewBookingsPerYear';
import TotalBookings from '../../../components/dashboards/modern/TotalBookings';
import CityWideAvailability from '../../../components/dashboards/modern/CityWideAvailabilty';
import ReactCountryFlag from "react-country-flag";
import CountryBox from '../../../components/CountryBox';
import DashboardCard from '../../../components/shared/DashboardCard';

// Country code mapping


// Separate CountryBox component

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [bookingsSummary, setBookingsSummary] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCountryId, setSelectedCountryId] = useState('');

  const [loading, setLoading] = useState(true);
  const [graphData, setGraphData] = useState(null);
  const [bookingsList, setBookingsList] = useState([])
  // Fetch data on component mount
  const [searchTerm, setSearchTerm] = useState('');
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [searchTermBookings, setSearchTermBookings] = useState('');

  useEffect(() => {
      const fetchBookings = async () => {
        try {
          setBookingsLoading(true);
          const response = await fget({ 
            url: `api/v2/bookings`, 
            params: { 
              limit: 10,
              term: searchTermBookings 
            } 
          });
          
          if (response.data.status === 'Success') {
            setBookingsList(response.data.bookings);
          }
        } catch (error) {
          console.error('Bookings fetch error:', error);
          toast.error('Failed to fetch bookings');
        } finally {
          setBookingsLoading(false);
        }
      };
  
      const debounceTimer = setTimeout(() => {
        fetchBookings();
      }, 500);
  
      return () => clearTimeout(debounceTimer);
    }, [searchTermBookings]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [bookingsResponse, dashboardResponse, bookingsList] = await Promise.all([
          fget({ url: '/api/v2/bookings/summary' }),
          fget({ url: '/api/v2/channelpartner/dashboard' }),
          fget({  url: `api/v2/bookings` }),
          // handleBookingSearch('')
        ]);

        if (dashboardResponse.data.status === 'Success' && bookingsResponse.data.status === 'Success') {
          const dashboard = dashboardResponse.data.data.map(country => ({
            ...country,
            bedsCount: country?.totalBeds
            // bedsCount: country?.collegeData?.reduce((total, town) => 
            //   total + (parseInt(town.bedSpaces) || 0), 0)
          }));
  
          setDashboardData(dashboard);
          setDashboardData(dashboard);
          setBookingsSummary(bookingsResponse.data.bookings);
          setGraphData(dashboardResponse.data.pagination);
          setBookingsList(bookingsList.data.bookings)
          // Set initial selected country
          if (!selectedCountry && dashboard.length > 0) {
            console.log('Setting initial country:', dashboard[0].countryName);
            setSelectedCountry(dashboard[0].countryName);
            setSelectedCountryId(dashboard[0].countryId);
          }
        }
      } catch (error) {
        console.error('Fetch Error:', error);
        toast.error('Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Loading state
  if (loading || !dashboardData || !bookingsSummary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Get current country data
  const countryData = dashboardData.find(
    country => country.countryName === selectedCountry
  ) || dashboardData[0];

  const bookingData = bookingsSummary.find(
    booking => booking.countryName === selectedCountry
  ) || bookingsSummary[0];

  return (
    <Box sx={{ mt: { xs: 1, sm: 2 } }}>
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Top Section */}
        <Grid item xs={12}>
          <DashboardCard sx={{ 
            p: { xs: 0, sm: 3 }, 
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 1
          }}>
            <Typography 
              variant="h4" 
              sx={{ 
                mb: { xs: 1.5, sm: 2 },
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Availability Overview
            </Typography>
            
            {/* Country Selection - Horizontal Scroll */}
            <Box sx={{
              mb: { xs: 2, sm: 3 },
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              overflowX: 'auto',
              pb: 1,
              '-webkit-overflow-scrolling': 'touch',
              '&::-webkit-scrollbar': { height: { xs: 4, sm: 8 } },
              '&::-webkit-scrollbar-track': { backgroundColor: 'grey.100', borderRadius: 2 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'grey.400', borderRadius: 2 }
            }}>
              {dashboardData.map((country) => (
                <CountryBox
                  key={country.countryName}
                  country={country}
                  selected={country.countryName === selectedCountry}
                  onClick={() => {
                    setSelectedCountry(country.countryName);
                    setSelectedCountryId(country.countryId);
                  }}
                  isDashboard={true}
                  bedsCount={country.bedsCount}
                />
              ))}
            </Box>
  
            {/* CityWide and TownWise */}
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={7}>
                <CityWideAvailability 
                  cityData={countryData.provinceData} 
                  countryId={selectedCountryId} 
                  country={selectedCountry}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TownWiseAvailability 
                  townData={countryData.collegeData} 
                  country={selectedCountryId}
                />
              </Grid>
            </Grid>
          </DashboardCard>
        </Grid>
  
        {/* Graphs Section */}
        <Grid item xs={12}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <NewBookingsGraph monthlyBookings={bookingData?.montlyBookings} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <NewBookingsPerYear totalBookings={graphData.totalBookings} />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <TotalBookings totalBookings={graphData.totalBookings} />
            </Grid>
          </Grid>
        </Grid>
  
        {/* Bottom Section */}
        <Grid item xs={12}>
          <NewBookings 
            bookingsData={bookingsList}
            searchTerm={searchTermBookings}
            setSearchTerm={setSearchTermBookings}
            loading={bookingsLoading}
            sx={{
              '& .MuiCard-root': {
                p: { xs: 2, sm: 3 }
              }
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;